import Layout from "../Layout";
import { BreadcrumbNavigation } from "../../components/BreadcrumbNavigation";
import { Tabs, Tab } from "../../components/ui/Tabs";
import { ComingSoon } from "../../components/ui/ComingSoon";
import { FormField } from "../../components/ui/form_field";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import {
  Info,
  Building2,
  ShieldCheck,
  Lock,
  ExternalLink,
  CheckCircle2,
  XCircle,
} from "lucide-react";
import { SiTerraform } from "@icons-pack/react-simple-icons";
import { CloudFormationIcon } from "../../components/icons/CloudFormationIcon";
import { useForm, router } from "@inertiajs/react";
import { useState } from "react";
import { useToast } from "../../components/ui/toast";
import { fetchWithCsrf } from "../../utils/csrf";
import { AwsConnectionDetailsCard } from "../../components/aws/AwsConnectionDetailsCard";
import { CodeSnippetWithCopy } from "../../components/ui/CodeSnippetWithCopy";
import { InlineCodeTag } from "../../components/ui/InlineCodeTag";
import { InfoAlert } from "../../components/ui/InfoAlert";
import { Terminal } from "lucide-react";

interface Workspace {
  name: string;
  slug: string;
}

interface NewAwsAccountProps {
  workspace: Workspace;
  external_id: string;
  assumer_role_arn: string;
  assuming_role_name: string;
}

export default function NewAwsAccount({
  workspace,
  external_id,
  assumer_role_arn,
  assuming_role_name,
}: NewAwsAccountProps) {
  const { data, setData, errors } = useForm({
    display_name: "",
    role_arn: "",
  });

  const [isVerifying, setIsVerifying] = useState(false);
  const [isVerified, setIsVerified] = useState(false);
  const [verificationError, setVerificationError] = useState<string | null>(
    null,
  );
  const [verificationSuccess, setVerificationSuccess] = useState<string | null>(
    null,
  );
  const { showToast } = useToast();

  const handleVerify = async () => {
    setIsVerifying(true);
    setVerificationError(null);
    setVerificationSuccess(null);

    try {
      const response = await fetchWithCsrf(
        `/workspaces/${workspace.slug}/aws_accounts/verify`,
        {
          method: "POST",
          body: JSON.stringify({
            aws_account: {
              display_name: data.display_name,
              role_arn: data.role_arn,
            },
          }),
        },
      );

      const responseData = await response.json();

      if (response.ok && responseData.success) {
        setVerificationSuccess(
          `Successfully connected to AWS account ${responseData.account_id}`,
        );
        setIsVerified(true);
        showToast("success", "AWS account verified successfully!");
      } else {
        if (responseData.errors) {
          setVerificationError(responseData.errors.join(", "));
          showToast("error", "Validation failed");
        } else if (responseData.error) {
          setVerificationError(responseData.error);
          showToast("error", "Connection failed");
        } else {
          setVerificationError("An unexpected error occurred");
          showToast("error", "An unexpected error occurred");
        }
      }
    } catch {
      setVerificationError("An unexpected error occurred");
      showToast("error", "An unexpected error occurred");
    } finally {
      setIsVerifying(false);
    }
  };

  const handleAddAccount = () => {
    router.post(`/workspaces/${workspace.slug}/aws_accounts`, {
      aws_account: {
        display_name: data.display_name,
        role_arn: data.role_arn,
      },
    });
  };

  const terraformSnippet = `module "infralyst_readonly_integration" {
  source  = "infralyst/readonly-integration/aws"
  version = "~> 1.0"
  external_id = "${external_id}"
}

output "infralyst_role_arn" {
  value       = module.infralyst_readonly_integration.role_arn
  description = "Paste this Role ARN into Infralyst"
}`;

  const terraformCommands = `terraform init
terraform plan
terraform apply`.trim();

  const tabs: Tab[] = [
    {
      id: "terraform",
      label: "Terraform",
      icon: SiTerraform,
      content: (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 p-6 border shadow-lg rounded-2xl bg-background/70 dark:bg-zinc-900/80 backdrop-blur">
            <h2 className="text-xl font-bold mb-4">
              Create a read-only IAM role with Terraform
            </h2>

            <ol className="space-y-3 mb-6 list-decimal list-inside text-gray-700 dark:text-gray-300">
              <li>
                Paste the snippet into your Terraform repository (e.g.,{" "}
                <InlineCodeTag>infralyst_integration.tf</InlineCodeTag>)
              </li>
              <li>
                Run <InlineCodeTag>terraform init</InlineCodeTag>,{" "}
                <InlineCodeTag>plan</InlineCodeTag>, and{" "}
                <InlineCodeTag>apply</InlineCodeTag>
              </li>
              <li>Copy the Role ARN output and paste it in the form below</li>
              <li>Click Verify connection to complete setup</li>
            </ol>

            <CodeSnippetWithCopy
              code={terraformSnippet}
              label="Terraform Configuration"
              ariaLabel="Copy Terraform snippet"
              language="hcl"
              showSyntaxHighlighting={true}
              fontSize="0.75rem"
            />

            <div className="mb-4">
              <a
                href="https://github.com/infralyst/terraform-aws-readonly-integration"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-sm text-primary hover:text-primary/80 transition-colors"
              >
                View module source on GitHub
                <ExternalLink className="w-4 h-4" />
              </a>
            </div>

            <InfoAlert icon={Lock} variant="info" className="mb-6">
              This module grants read-only access only. Review the source code
              on GitHub to verify.
            </InfoAlert>

            <CodeSnippetWithCopy
              code={terraformCommands}
              label="Commands"
              ariaLabel="Copy terraform commands"
              showSyntaxHighlighting={false}
            />

            <p className="text-sm text-gray-500 dark:text-gray-400">
              Remove access by destroying these resources or removing the file
              and running <InlineCodeTag>terraform apply</InlineCodeTag>.
            </p>
          </div>

          <div className="lg:col-span-1">
            <AwsConnectionDetailsCard
              externalId={external_id}
              assumerRoleArn={assumer_role_arn}
              assumingRoleName={assuming_role_name}
            />
          </div>
        </div>
      ),
    },
    {
      id: "cloudformation",
      label: "CloudFormation",
      icon: CloudFormationIcon,
      content: (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
            <ComingSoon feature="CloudFormation" />
          </div>
          <div className="lg:col-span-1">
            <AwsConnectionDetailsCard
              externalId={external_id}
              assumerRoleArn={assumer_role_arn}
              assumingRoleName={assuming_role_name}
            />
          </div>
        </div>
      ),
    },
    {
      id: "manual",
      label: "Manual",
      icon: Terminal,
      content: (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
            <ComingSoon feature="Manual setup" />
          </div>
          <div className="lg:col-span-1">
            <AwsConnectionDetailsCard
              externalId={external_id}
              assumerRoleArn={assumer_role_arn}
              assumingRoleName={assuming_role_name}
            />
          </div>
        </div>
      ),
    },
  ];

  return (
    <Layout
      title="Add AWS Account"
      description="Set up AWS account connection with cross-account IAM role"
    >
      <section className="px-4 py-16 pt-40">
        <div className="max-w-4xl mx-auto">
          <BreadcrumbNavigation
            items={[
              { label: "Workspaces", href: "/workspaces" },
              { label: workspace.name, href: `/workspaces/${workspace.slug}` },
              { label: "Add AWS account" },
            ]}
          />

          <div className="text-center mb-12">
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold leading-tight tracking-tight">
              Add a new <span className="text-primary">AWS</span> account
            </h1>
          </div>

          <div className="mb-8">
            <Tabs tabs={tabs} defaultTab="terraform" />
          </div>

          <div className="space-y-6">
            <div className="p-6 border shadow-lg rounded-2xl bg-background/70 dark:bg-zinc-900/80 backdrop-blur">
              <FormField
                id="display_name"
                label="Display name"
                error={errors.display_name}
              >
                <Input
                  id="display_name"
                  type="text"
                  placeholder="e.g., AWS Production"
                  value={data.display_name}
                  onChange={(e) => setData("display_name", e.target.value)}
                  aria-invalid={!!errors.display_name}
                  maxLength={80}
                  leftIcon={<Building2 className="w-5 h-5 text-primary" />}
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Shown in lists. You can change it later.
                </p>
              </FormField>

              <div className="mt-6">
                <FormField
                  id="role_arn"
                  label="Role ARN"
                  error={errors.role_arn}
                >
                  <Input
                    id="role_arn"
                    type="text"
                    placeholder={`arn:aws:iam::123456789012:role/${assuming_role_name}`}
                    value={data.role_arn}
                    onChange={(e) => setData("role_arn", e.target.value)}
                    aria-invalid={!!errors.role_arn}
                    className="font-mono text-sm"
                    leftIcon={<ShieldCheck className="w-5 h-5 text-primary" />}
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    We'll assume this role using the External ID assigned to
                    your workspace.
                  </p>
                  <div aria-live="polite" role="status" className="mt-2">
                    {/* Success/error messages will appear here */}
                  </div>
                </FormField>
              </div>

              <InfoAlert icon={Info} variant="info" className="mt-4">
                No access keys. We assume this role via short-lived STS +
                External ID.
              </InfoAlert>

              {verificationSuccess && (
                <div className="mt-4 p-4 border rounded-lg bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800">
                  <div className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <h3 className="text-sm font-semibold text-green-800 dark:text-green-200 mb-1">
                        Connection successful
                      </h3>
                      <p className="text-sm text-green-700 dark:text-green-300">
                        {verificationSuccess}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {verificationError && (
                <div className="mt-4 p-4 border rounded-lg bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800">
                  <div className="flex items-start gap-3">
                    <XCircle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <h3 className="text-sm font-semibold text-red-800 dark:text-red-200 mb-1">
                        Connection failed
                      </h3>
                      <p className="text-sm text-red-700 dark:text-red-300">
                        {verificationError}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              <div className="mt-6 flex justify-end">
                <Button
                  type="button"
                  onClick={isVerified ? handleAddAccount : handleVerify}
                  disabled={
                    !data.role_arn ||
                    !data.display_name ||
                    isVerifying
                  }
                  isLoading={isVerifying}
                  loadingText="Verifying..."
                  className="w-full sm:w-auto"
                >
                  {isVerified ? "Add account" : "Verify connection"}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
}
